import { create } from 'zustand';
import { settingsService } from '@/services/settingsService';
import { BusinessIdentity, DEFAULT_BUSINESS_IDENTITY } from '@/types/identity';
import { logger } from '@/lib/utils/logger';

interface IdentityState {
  identity: BusinessIdentity;
  isLoading: boolean;
  error: string | null;
  fetchIdentity: () => Promise<void>;
  updateIdentity: (updates: Partial<BusinessIdentity>) => Promise<void>;
}

export const useIdentityStore = create<IdentityState>((set, get) => ({
  identity: DEFAULT_BUSINESS_IDENTITY,
  isLoading: false,
  error: null,

  fetchIdentity: async () => {
    set({ isLoading: true, error: null });
    try {
      // Ensure settingsService is initialized (this also fetches the raw identity cache)
      await settingsService.initialize();
      
      const raw = settingsService.rawIdentityCache;
      const companyProfile = settingsService.getCompanyProfile();
      
      if (raw) {
        set({ 
          identity: { ...DEFAULT_BUSINESS_IDENTITY, ...raw },
          isLoading: false 
        });
      } else {
        // Map from companyProfile
        const seedFromProfile: Partial<BusinessIdentity> = {};
        if (companyProfile) {
          seedFromProfile.company_name = companyProfile.name || DEFAULT_BUSINESS_IDENTITY.company_name;
          seedFromProfile.legal_name = companyProfile.legal_name || companyProfile.name;
          seedFromProfile.contact_phone = companyProfile.phone;
          seedFromProfile.contact_email = companyProfile.email;
          seedFromProfile.address = companyProfile.address;
          seedFromProfile.logo_url = companyProfile.logo_url;
          seedFromProfile.gstin = companyProfile.gst_number;
        }
        
        const initialIdentity = { ...DEFAULT_BUSINESS_IDENTITY, ...seedFromProfile };
        
        // Save the seeded identity back to remote so it's initialized on remote database too
        try {
          await settingsService.saveToRemote('business_identity', initialIdentity);
          settingsService.rawIdentityCache = initialIdentity;
        } catch (saveErr) {
          logger.warn('Failed to auto-seed business identity to database:', saveErr);
        }

        set({ identity: initialIdentity, isLoading: false });
      }
    } catch (error) {
      logger.error('Failed to fetch business identity:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },
 
  updateIdentity: async (updates) => {
    try {
      const current = get().identity;
      const next: BusinessIdentity = {
        ...current,
        ...updates,
        branding_version: (current.branding_version || 1) + 1,
      };

      // Immediately sync rawIdentityCache in settingsService to prevent race condition overrides
      settingsService.rawIdentityCache = next;
      set({ identity: next });

      // Save to remote via settings service
      await settingsService.saveToRemote('business_identity', next);

      // Synchronize company profile settings as well to keep company headers in sync
      try {
        const currentProfile = settingsService.getCompanyProfile();
        const updatedProfile = {
          ...currentProfile,
          name: next.company_name || currentProfile.name,
          legal_name: next.legal_name || currentProfile.legal_name || next.company_name,
          phone: next.contact_phone || currentProfile.phone,
          email: next.contact_email || currentProfile.email,
          address: next.address || currentProfile.address,
          gst_number: next.gstin || currentProfile.gst_number,
          logo_url: next.logo_url || currentProfile.logo_url
        };
        // Directly update company_profile remote setting without triggering a recursive business_identity overwrite
        await settingsService.saveToRemote('company_profile', updatedProfile);
      } catch (syncErr) {
        logger.warn('Failed to sync business identity changes to company profile:', syncErr);
      }
    } catch (error) {
      logger.error('Failed to update business identity:', error);
      throw error;
    }
  }
}));
